import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

import { ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useSizeTypes } from 'contexts';
import useRedeliveryApi from 'pages/redeliveries/use-redelivery-api';
import { getUnitByUnitNumber } from 'services';
import { BookingDetails, RedeliveryDetail } from 'types';
import GateUnit from 'types/gate-unit';
import { useBookingApi } from './use-booking-api';
import { useUnitApi } from './use-unit-api';

export type ReservationUnit = Partial<Pick<GateUnit, 'equipmentType' | 'sizeType' | 'unitNumber'>>;

interface ValidateReservationProps {
  customerId?: number;
  locationId?: number;
  bookingDetails?: BookingDetails[];
  redeliveryDetails?: RedeliveryDetail[];
  setUnit: Dispatch<SetStateAction<ReservationUnit>>;
}

export const useReservation = ({
  customerId,
  locationId,
  bookingDetails,
  redeliveryDetails,
  setUnit
}: ValidateReservationProps) => {
  const { findBookingByUnitId } = useBookingApi();
  const { findRedeliveryByUnitId } = useRedeliveryApi();
  const { findReservedUnit } = useUnitApi();
  const { getEquipmentType } = useSizeTypes();

  const isBooking = useMemo(() => !!bookingDetails?.length, [bookingDetails]);
  const isRedelivery = useMemo(() => !!redeliveryDetails?.length, [redeliveryDetails]);

  const sizeTypeSet = useMemo(() => {
    const sizeTypes = new Set<string>();
    bookingDetails?.forEach(({ iso }) => iso?.forEach(sizeType => sizeType && sizeTypes.add(sizeType)));
    redeliveryDetails?.forEach(({ sizeType }) => sizeType && sizeTypes.add(sizeType));
    return sizeTypes;
  }, [bookingDetails, redeliveryDetails]);

  const handleSetUnit = useCallback(
    (sizeType?: string | null, unitNumber?: string | null) => {
      setUnit(prevState => ({
        equipmentType: getEquipmentType(sizeType ?? prevState.sizeType) ?? prevState.equipmentType,
        sizeType: sizeType ?? prevState.sizeType,
        unitNumber: unitNumber ?? prevState.unitNumber
      }));
    },
    [getEquipmentType, setUnit]
  );

  const validateUnitNumber = useCallback(
    async (input: string): Promise<ValidationResult> => {
      if (!input) return { status: ValidationStatus.DEFAULT };
      const activeUnit = await getUnitByUnitNumber(input);
      if (activeUnit) {
        if (activeUnit.customer && activeUnit.customer.id !== customerId) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already in use by ${activeUnit.customer.name}`
          };
        }
        if (activeUnit.location && activeUnit.location.id !== locationId) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already in use at ${activeUnit.location.name}`
          };
        }
        if (activeUnit.sizeType && !sizeTypeSet.has(activeUnit.sizeType)) {
          return {
            status: ValidationStatus.WARNING,
            error: `${isBooking ? 'Booking' : 'Redelivery'} does not include size type ${activeUnit.sizeType}`
          };
        }
        if (isBooking) {
          const unitBooking = await findBookingByUnitId(activeUnit.id);
          if (unitBooking) {
            return {
              status: ValidationStatus.WARNING,
              error: `Unit already assigned to booking #${unitBooking.bookingNumber}`
            };
          }
        }
        if (isRedelivery) {
          const unitRedelivery = await findRedeliveryByUnitId(activeUnit.id);
          if (unitRedelivery) {
            return {
              status: ValidationStatus.WARNING,
              error: `Unit already assigned to redelivery #${unitRedelivery.redeliveryNumber}`
            };
          }
        }

        handleSetUnit(activeUnit.sizeType, activeUnit.unitNumber);
        return { status: ValidationStatus.SUCCESS };
      }
      const reservedUnit = await findReservedUnit(input);
      if (reservedUnit) {
        if (reservedUnit.customer && reservedUnit.customer.id !== customerId) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already reserved by ${reservedUnit.customer.name}`
          };
        }
        if (reservedUnit.location && reservedUnit.location.id !== locationId) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already reserved at ${reservedUnit.location.name}`
          };
        }
        if (reservedUnit.sizeType && !sizeTypeSet.has(reservedUnit.sizeType)) {
          return {
            status: ValidationStatus.WARNING,
            error: `${isBooking ? 'Booking' : 'Redelivery'} does not include size type ${reservedUnit.sizeType}`
          };
        }
        if (isBooking && reservedUnit.booking) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already assigned to booking #${reservedUnit.booking.bookingNumber}`
          };
        }
        if (isRedelivery && reservedUnit.redelivery) {
          return {
            status: ValidationStatus.WARNING,
            error: `Unit already assigned to redelivery #${reservedUnit.redelivery.redeliveryNumber}`
          };
        }
        handleSetUnit(reservedUnit.sizeType, reservedUnit.unitNumber);
      }
      return { status: ValidationStatus.SUCCESS };
    },
    [
      customerId,
      findBookingByUnitId,
      findRedeliveryByUnitId,
      findReservedUnit,
      handleSetUnit,
      isBooking,
      isRedelivery,
      locationId,
      sizeTypeSet
    ]
  );

  return { handleSetUnit, validateUnitNumber };
};

export default useReservation;
